import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import {
  DEFAULT_APPEARANCE,
  type AppearanceMode,
  type ResolvedAppearance,
} from '../ui/theme/appearance';
import {
  applyAppearance,
  readMirroredAppearance,
  writeMirroredAppearance,
} from '../ui/theme/applyAppearance';
import { DEFAULT_ACCENT_COLOR, type AccentColor } from '../ui/theme/accentColor';
import {
  applyAccentColor,
  readMirroredAccentColor,
  writeMirroredAccentColor,
} from '../ui/theme/applyAccentColor';
import { settingsRepository } from '../data/repositories/settingsRepository';

interface ThemeContextValue {
  mode: AppearanceMode;
  resolved: ResolvedAppearance;
  setMode: (mode: AppearanceMode) => void;
  accentColor: AccentColor;
  setAccentColor: (color: AccentColor) => void;
  /** False until the persisted value has been loaded from IndexedDB. */
  ready: boolean;
}

const ThemeContext = createContext<ThemeContextValue | undefined>(undefined);

export function ThemeProvider({ children }: { children: ReactNode }): React.JSX.Element {
  // Seed from the synchronous mirror so the first render already matches what
  // the pre-paint script applied. IndexedDB then confirms or corrects it.
  const [mode, setModeState] = useState<AppearanceMode>(() => readMirroredAppearance());
  const [accentColor, setAccentColorState] = useState<AccentColor>(() => readMirroredAccentColor());
  const [ready, setReady] = useState(false);

  // Load the authoritative value from the database.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        // `getAppearance()` also migrates a pre-upgrade `system` value (or
        // anything else invalid) to a concrete choice and persists it to
        // IndexedDB, the authoritative store — deliberately not mirrored to
        // localStorage here too: the mirror exists only to avoid a pre-paint
        // flash, a stale 'system' mirror already resolves correctly and
        // deterministically on its own (see index.html's script), and
        // writing to localStorage on every load rather than only on an
        // explicit choice is an unrelated behaviour change other tests
        // depend on (App.auth.test.tsx's "nothing but the auth cache is
        // written to storage on login" guarantee).
        const [storedMode, storedAccent] = await Promise.all([
          settingsRepository.getAppearance(),
          settingsRepository.getAccentColor(),
        ]);
        if (!cancelled) {
          setModeState(storedMode);
          setAccentColorState(storedAccent);
        }
      } catch {
        // A settings read failure must not stop the app; the mirror stands.
        if (!cancelled) {
          setModeState(readMirroredAppearance());
          setAccentColorState(readMirroredAccentColor());
        }
      } finally {
        if (!cancelled) setReady(true);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Apply to the document whenever the mode or accent colour changes.
  useEffect(() => {
    applyAppearance(mode);
  }, [mode]);

  useEffect(() => {
    applyAccentColor(accentColor);
  }, [accentColor]);

  const value = useMemo<ThemeContextValue>(
    () => ({
      mode,
      resolved: mode,
      accentColor,
      ready,
      setMode: (next: AppearanceMode) => {
        // Apply immediately, persist in the background: the interface must
        // never wait on a database write to change colour.
        setModeState(next);
        writeMirroredAppearance(next);
        void settingsRepository.setAppearance(next).catch(() => undefined);
      },
      setAccentColor: (next: AccentColor) => {
        setAccentColorState(next);
        writeMirroredAccentColor(next);
        void settingsRepository.setAccentColor(next).catch(() => undefined);
      },
    }),
    [mode, accentColor, ready],
  );

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme(): ThemeContextValue {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used inside a ThemeProvider');
  }
  return context;
}

export { DEFAULT_APPEARANCE, DEFAULT_ACCENT_COLOR };
